import React, { useCallback, useEffect, useState } from "react";
import Button from "../Button";
import { LocationSelectedProps } from "./types";
import i18next from "i18next";
import { getOpeningTimesObject } from "~/assets/js/Helpers/util";
import { getFirstAvailableTimeslot } from "~/assets/js/Helpers/timeslot-utils";
import StoreHintPopup from "../StoreHint/StoreHintPopup";
import { isToday, getLocalisedDayOfWeek } from "../../Helpers/date-utils";
import cartSession from "../../global/cartSession";
import { formatCheckoutPrice } from "../../Format";

const STORE_STATUS = {
    CLOSED: "closed",
    OPENING_SOON: "opening_soon",
    OPEN: "open",
};

const FIVE_MINUTES = 5 * 60 * 1000;
const ONE_MINUTE = 1 * 60 * 1000;

export const LocationSelected = ({
    address,
    setOverlayOpen,
    icon,
    storeInformation,
    isXXL = false,
    fetchStoreData,
}) => {
    const [storeStatus, setStoreStatus] = useState(STORE_STATUS.CLOSED);
    const [firstDeliveryTime, setFirstDelivery] = useState(undefined);
    const [openPopup, setOpenPopup] = useState(false);
    const cartSessionData = cartSession.get();

    /**
     * Refetch store data (timeslots) on an interval
     */
    useEffect(() => {
        const storeInformationInterval = setInterval(
            fetchStoreData,
            FIVE_MINUTES
        );
        return () => clearInterval(storeInformationInterval);
    }, []);

    /**
     * Handle store status calculation
     */
    useEffect(() => {
        if (!storeInformation) {
            return;
        }

        const currentStatus = getOpeningTimesObject(
            storeInformation.openingTimes
        );

        if (currentStatus) {
            setStoreStatus(currentStatus.status);
        }

        const { deliveryTimes } = storeInformation;
        const timeslot = getFirstAvailableTimeslot(deliveryTimes, false, true);

        setFirstDelivery(timeslot);

        //Ensure that valid till condition is still met if user is idling on the page.
        const validTillCheck = (deliveryTimes, timeslot) => {
            const firstDeliveryTime = getFirstAvailableTimeslot(
                deliveryTimes,
                false,
                true
            );

            if (firstDeliveryTime.id !== timeslot.id) {
                setFirstDelivery(firstDeliveryTime);
            }
        };

        const validTillInterval = setInterval(
            () => validTillCheck(deliveryTimes, timeslot),
            ONE_MINUTE
        );

        return () => clearInterval(validTillInterval);
    }, [storeInformation]);

    const renderOpenStoreStatus = () => {
        // use case 1 ~ next timeslot is today
        if (isToday(firstDeliveryTime.deliveryDay)) {
            return (
                <div className="flex">
                    <small className="select-location__shop-info--open bold">
                        {i18next.t("topbar.label.earliest")}{" "}
                        {firstDeliveryTime.timeSlot}
                    </small>
                    {cartSessionData?.shipping?.minimal_value > 0 && (
                        <small className="thin">
                            (Mindestbestellwert{" "}
                            {formatCheckoutPrice(
                                cartSessionData?.shipping?.minimal_value,
                                null,
                                "€",
                                null
                            )}
                        </small>
                    )}
                </div>
            );
        }

        // use case 2 ~ no more timeslots today
        const nextOpeningDay = getLocalisedDayOfWeek(
            firstDeliveryTime.deliveryDay
        );

        return (
            <small className="select-location__shop-info--closed bold">
                <small className="thin">
                    {i18next.t("topbar.label.xxlDelivery", {
                        day: nextOpeningDay,
                    })}
                </small>
            </small>
        );
    };

    const renderStoreStatus = useCallback(() => {
        if (!firstDeliveryTime) {
            return;
        }

        // Handle XXL area use cases
        if (isXXL) {
            return renderOpenStoreStatus();
        }

        switch (storeStatus) {
            case STORE_STATUS.CLOSED:
                return (
                    <small className="select-location__shop-info--closed bold">
                        {i18next.t("topbar.label.closed")}{" "}
                        <small className="thin">
                            ({i18next.t("topbar.label.canPreorder")})
                        </small>
                    </small>
                );
            case STORE_STATUS.OPENING_SOON:
                return (
                    <small className="select-location__shop-info--open bold">
                        {i18next.t("topbar.label.openingSoon")}{" "}
                        <small className="thin">
                            ({i18next.t("topbar.label.canPreorder")})
                        </small>
                    </small>
                );
            case STORE_STATUS.OPEN:
                if (!storeInformation) {
                    return null;
                }
                return renderOpenStoreStatus();
        }
    }, [storeStatus, firstDeliveryTime, isXXL]);

    const openDeliveryTimePopup = () => {
        if (isXXL || storeStatus === STORE_STATUS.OPEN) {
            return;
        }

        setOpenPopup(true);
    };

    const closeDeliveryTimePopup = () => setOpenPopup(false);

    return (
        <>
            <div className="select-location">
                <img
                    src={icon}
                    alt="delivery-icon"
                    className="select-location__icon"
                />
                <div
                    className="select-location__address-wrapper"
                    onClick={openDeliveryTimePopup}>
                    <strong className="select-location__address--elipsis ph-no-capture">
                        {address}
                    </strong>
                    <div className="select-location__shop-info">
                        {renderStoreStatus()}
                    </div>
                </div>

                <Button
                    customStyle="select-location__button"
                    label={i18next.t("topbar.label.change")}
                    onClick={() => setOverlayOpen(true)}
                    variant="secondary"
                    size="XS"
                />
            </div>
            {firstDeliveryTime && (
                <StoreHintPopup
                    storeStatus={storeStatus}
                    open={openPopup}
                    handleClose={closeDeliveryTimePopup}
                    firstDeliveryTime={firstDeliveryTime}
                />
            )}
        </>
    );
};

LocationSelected.propTypes = LocationSelectedProps;
